/**
 * Amortisation — types and the Schedule II useful life table.
 *
 * One engine covers four things that are the same arithmetic wearing different
 * clothes: prepaid expenses released to P&L, deferred revenue recognised over a
 * service period, fixed assets depreciated, and loans split between principal
 * and interest.
 *
 * Indian practice needs two depreciation answers at once and they rarely agree:
 * Schedule II of the Companies Act drives the financial statements, while the
 * Income Tax Act's block-of-assets method drives the tax computation. Keeping
 * both is not optional — the difference between them is the deferred tax.
 */

export type ScheduleKind =
  | "prepaid"
  | "deferred-revenue"
  | "accrual"
  | "fixed-asset"
  | "intangible"
  | "loan"
  | "lease";

export type AmortisationMethod =
  | "straight-line"
  | "straight-line-daily"
  | "written-down-value"
  | "units-of-production"
  | "effective-interest"
  | "immediate";

export type PeriodFrequency = "monthly" | "quarterly" | "half-yearly" | "annually";

export interface SchedulePeriod {
  /** 1-based sequence within the schedule. */
  sequence: number;
  /** Period label, e.g. "Aug 2026" or "FY 2026-27". */
  label: string;
  from: string;
  to: string;
  /** Days in the period actually covered — matters for pro-rata openings. */
  days: number;
  openingBalance: number;
  /** Charge for the period. */
  amount: number;
  closingBalance: number;
  /** Cumulative charge to date. */
  cumulative: number;
  posted: boolean;
  postedOn?: string;
  journalId?: string;
}

export interface AmortisationSchedule {
  id: string;
  name: string;
  kind: ScheduleKind;
  method: AmortisationMethod;
  frequency: PeriodFrequency;
  /** Amount being spread — cost for an asset, premium for a prepaid. */
  grossAmount: number;
  /** Amount not depreciated — salvage on an asset, nil on a prepaid. */
  residualValue: number;
  startDate: string;
  endDate: string;
  /** Balance-sheet account the schedule sits in. */
  balanceAccountCode: string;
  /** P&L account the periodic charge posts to. */
  chargeAccountCode: string;
  periods: SchedulePeriod[];
  totalCharge: number;
  postedToDate: number;
  remaining: number;
  status: "Draft" | "Active" | "Completed" | "Cancelled";
  notes?: string;
}

/**
 * Schedule II, Part C useful lives. The Act permits a different life where it
 * is justified by technical assessment, and that justification must be
 * disclosed — hence `basis` on the asset record rather than silent override.
 */
export interface UsefulLifeEntry {
  key: string;
  category: string;
  asset: string;
  /** Useful life in years under Schedule II. */
  years: number;
  note?: string;
}

export const SCHEDULE_II_LIVES: UsefulLifeEntry[] = [
  { key: "building-rcc", category: "Buildings", asset: "Buildings other than factory, RCC frame", years: 60 },
  { key: "building-non-rcc", category: "Buildings", asset: "Buildings other than factory, non-RCC frame", years: 30 },
  { key: "building-factory", category: "Buildings", asset: "Factory buildings", years: 30 },
  { key: "building-temporary", category: "Buildings", asset: "Temporary erections such as wooden structures", years: 3 },
  { key: "plant-general", category: "Plant and machinery", asset: "General plant and machinery", years: 15, note: "Where no special rate applies. Triple-shift working increases depreciation by 100% for that period; double shift by 50%." },
  { key: "plant-continuous", category: "Plant and machinery", asset: "Continuous process plant", years: 25 },
  { key: "plant-rmc", category: "Plant and machinery", asset: "Concrete batching and RMC plant", years: 15 },
  { key: "furniture", category: "Furniture and fittings", asset: "General furniture and fittings", years: 10 },
  { key: "furniture-hotel", category: "Furniture and fittings", asset: "Furniture used in hotels, restaurants and boarding houses", years: 8 },
  { key: "office-equipment", category: "Office equipment", asset: "Office equipment", years: 5 },
  { key: "computer-enduser", category: "Computers", asset: "End-user devices — desktops, laptops", years: 3 },
  { key: "computer-server", category: "Computers", asset: "Servers and networks", years: 6 },
  { key: "vehicle-motorcycle", category: "Vehicles", asset: "Motorcycles, scooters and mopeds", years: 10 },
  { key: "vehicle-car", category: "Vehicles", asset: "Motor cars other than those used in a hiring business", years: 8 },
  { key: "vehicle-commercial", category: "Vehicles", asset: "Motor buses, lorries and taxies used in a hiring business", years: 6 },
  { key: "vehicle-goods", category: "Vehicles", asset: "Motor lorries used other than in a hiring business", years: 8 },
  { key: "lab-equipment", category: "Laboratory", asset: "Laboratory equipment", years: 10 },
  { key: "electrical", category: "Installations", asset: "Electrical installations and equipment", years: 10 },
  { key: "solar", category: "Installations", asset: "Solar power generating systems", years: 25 },
  { key: "leasehold-improvement", category: "Improvements", asset: "Leasehold improvements", years: 0, note: "Amortised over the lease term rather than a Schedule II life." }
];

export const LIFE_INDEX = new Map(SCHEDULE_II_LIVES.map((entry) => [entry.key, entry]));

/**
 * Income Tax Act block-of-assets rates under rule 5. Depreciation is on the
 * written-down value of the whole block, not of individual assets — an asset
 * sold does not produce a gain or loss, it reduces the block.
 */
export interface ItBlock {
  key: string;
  block: string;
  rate: number;
  /** Additional depreciation under section 32(1)(iia) may apply. */
  additionalEligible?: boolean;
  note?: string;
}

export const IT_BLOCKS: ItBlock[] = [
  { key: "building-5", block: "Buildings — residential other than hotels and boarding houses", rate: 5 },
  { key: "building-10", block: "Buildings — other than residential", rate: 10 },
  { key: "building-40", block: "Buildings — purely temporary erections", rate: 40 },
  { key: "furniture-10", block: "Furniture and fittings", rate: 10 },
  { key: "plant-15", block: "Plant and machinery — general", rate: 15, additionalEligible: true },
  { key: "plant-30", block: "Plant and machinery — motor buses, lorries and taxis used in a hiring business", rate: 30 },
  { key: "plant-40", block: "Plant and machinery — computers and software", rate: 40 },
  { key: "plant-45", block: "Plant and machinery — motor cars acquired and put to use", rate: 45, note: "Rate applies to specified acquisition windows; otherwise 15%." },
  { key: "intangible-25", block: "Intangible assets — know-how, patents, copyrights, trademarks, licences, franchises", rate: 25 }
];

export const IT_BLOCK_INDEX = new Map(IT_BLOCKS.map((entry) => [entry.key, entry]));

export interface FixedAsset {
  id: string;
  code: string;
  name: string;
  category: string;
  /** Schedule II life key. */
  lifeKey?: string;
  /** Income Tax block key. */
  itBlockKey?: string;
  /** Date the asset was put to use — not the invoice date. */
  putToUseDate: string;
  cost: number;
  /** Additions to cost after capitalisation, e.g. improvements. */
  additions?: Array<{ date: string; amount: number; note?: string }>;
  /** Companies Act residual, capped at 5% of cost. */
  residualPercent?: number;
  /** Overrides the Schedule II life; requires a documented technical basis. */
  usefulLifeYears?: number;
  usefulLifeBasis?: string;
  method: "straight-line" | "written-down-value";
  /** Accumulated depreciation brought forward on migration. */
  openingAccumulated?: number;
  openingWdv?: number;
  /** Shift working in the period, which increases the Companies Act charge. */
  shift?: "single" | "double" | "triple";
  disposedOn?: string;
  disposalProceeds?: number;
  location?: string;
  costCentre?: string;
  assetAccountCode: string;
  accumulatedAccountCode: string;
  depreciationAccountCode: string;
  active: boolean;
}
